import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { transactionAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Package, TruckIcon, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const Transactions = () => {
  const [activeTab, setActiveTab] = useState('purchases');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // fetchTransactions depends on activeTab state

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await transactionAPI.getTransactions({
        type: activeTab === 'purchases' ? 'buyer' : 'seller',
      });
      setTransactions(response.data.data);
    } catch (err) {
      setError('Failed to load transactions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <Clock size={16} style={{ color: 'var(--warning)' }} />;
      case 'shipped':
        return <TruckIcon size={16} style={{ color: 'var(--primary)' }} />;
      case 'delivered':
        return <Package size={16} style={{ color: 'var(--primary)' }} />;
      case 'completed':
        return <CheckCircle size={16} style={{ color: 'var(--success)' }} />;
      case 'disputed':
        return <AlertCircle size={16} style={{ color: 'var(--error)' }} />;
      default:
        return <Clock size={16} style={{ color: 'var(--textSecondary)' }} />;
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'var(--warning)';
      case 'shipped':
      case 'delivered':
        return 'var(--primary)';
      case 'completed':
        return 'var(--success)';
      case 'disputed':
      case 'cancelled':
        return 'var(--error)';
      default:
        return 'var(--textSecondary)';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (autoReleaseDate) => {
    if (!autoReleaseDate) return null;
    const now = new Date();
    const release = new Date(autoReleaseDate);
    const diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
      <h1 style={{ marginBottom: '24px' }}>My Transactions</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', borderBottom: '2px solid var(--border)' }}>
        <button
          onClick={() => setActiveTab('purchases')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            borderBottom: `3px solid ${activeTab === 'purchases' ? 'var(--primary)' : 'transparent'}`,
            color: activeTab === 'purchases' ? 'var(--primary)' : 'var(--textSecondary)',
            fontWeight: activeTab === 'purchases' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          Purchases
        </button>
        <button
          onClick={() => setActiveTab('sales')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'none',
            borderBottom: `3px solid ${activeTab === 'sales' ? 'var(--primary)' : 'transparent'}`,
            color: activeTab === 'sales' ? 'var(--primary)' : 'var(--textSecondary)',
            fontWeight: activeTab === 'sales' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px',
          }}
        >
          Sales
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Transaction List */}
      {transactions.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Package size={48} style={{ margin: '0 auto 16px', color: 'var(--textSecondary)' }} />
          <h3 style={{ marginBottom: '8px' }}>No transactions yet</h3>
          <p className="text-muted">
            {activeTab === 'purchases'
              ? "You haven't made any purchases yet"
              : "You haven't made any sales yet"}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {transactions.map((transaction) => {
            const daysRemaining = getDaysRemaining(transaction.autoReleaseDate);

            return (
              <Link
                key={transaction._id}
                to={`/transactions/${transaction._id}`}
                className="card"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <div style={{ display: 'flex', gap: '16px' }}>
                  {/* Item Image */}
                  <img
                    src={transaction.item?.images?.[0]?.thumbnail || transaction.item?.images?.[0]?.fullSize}
                    alt={transaction.item?.title}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                    }}
                  />

                  {/* Transaction Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <h3 style={{ fontSize: '16px', marginBottom: '4px' }}>
                        {transaction.item?.title}
                      </h3>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          backgroundColor: 'var(--secondary)',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: getStatusColor(transaction.status),
                        }}
                      >
                        {getStatusIcon(transaction.status)}
                        {getStatusText(transaction.status)}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px', color: 'var(--textSecondary)' }}>
                      <span>
                        {activeTab === 'purchases' ? 'Seller' : 'Buyer'}:{' '}
                        <strong>{activeTab === 'purchases' ? transaction.seller?.username : transaction.buyer?.username}</strong>
                      </span>
                      <span>•</span>
                      <span>{formatDate(transaction.createdAt)}</span>
                      <span>•</span>
                      <span style={{ textTransform: 'capitalize' }}>
                        {transaction.purchaseType?.replace('-', ' ')}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)' }}>
                        ${(transaction.amount / 100).toFixed(2)}
                      </div>

                      {/* Escrow Warning */}
                      {transaction.fundsHeldInEscrow && activeTab === 'purchases' && transaction.status === 'delivered' && daysRemaining !== null && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          backgroundColor: '#FEF3C7',
                          fontSize: '12px',
                          color: '#92400E',
                        }}>
                          <Clock size={14} />
                          {daysRemaining > 0 ? `${daysRemaining} days to confirm` : 'Auto-release today'}
                        </div>
                      )}

                      {/* Seller Pending */}
                      {transaction.fundsHeldInEscrow && activeTab === 'sales' && transaction.status !== 'completed' && (
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--textSecondary)',
                          fontStyle: 'italic',
                        }}>
                          Payment in escrow
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Transactions;
